import { EnhancedMarkdown } from '@/components/ui/EnhancedMarkdown'
import { ChevronDown, Download, ExternalLink } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { parseCsv, rowsToObjects } from './utils'

function extractDomainFromUrl(url: string): string {
  if (!url) return ''
  try {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return new URL(url).hostname
    }
    // If no protocol, assume it's already a domain
    return url.split('/')[0]
  } catch {
    return url.split('/')[0] || ''
  }
}

function generateBacklinksSummaryMarkdown(
  target: string,
  targetType: string,
  action: string,
  database: string,
  totalBacklinks: number,
  recordCount: number,
  executedAt: string,
  backlinks: BacklinkData[],
  topDomains: TopDomainData[]
): string {
  const followLinks = backlinks.filter((b) => b.dofollow === 'Yes').length
  const nofollowLinks = backlinks.filter((b) => b.dofollow === 'No').length
  const avgAuthority =
    backlinks.length > 0
      ? Math.round(
          backlinks.reduce(
            (sum, b) => sum + Math.max(b.domain_authority || 0, b.page_authority || 0),
            0
          ) / backlinks.length
        )
      : 0
  const highAuthorityLinks = backlinks.filter(
    (b) => Math.max(b.domain_authority || 0, b.page_authority || 0) >= 50
  ).length
  const brokenLinks = backlinks.filter((b) => b.broken_status === 'Yes').length
  const newLinks = backlinks.filter((b) => b.link_is_new === 'Yes').length
  const lostLinks = backlinks.filter((b) => b.link_is_lost === 'Yes').length

  return `## 🔗 Backlinks Analysis

**Target:** ${target} (${targetType})
**Database:** ${database} • **Action:** ${action}
**Executed:** ${new Date(executedAt).toLocaleString()}

### 📊 Summary Stats

| Metric | Value |
|--------|-------|
| 🎯 **Total Backlinks** | ${totalBacklinks} |
| ⭐ **Average Authority** | ${avgAuthority}/100 |
| 🟢 **Dofollow Links** | ${followLinks} |
| 🟡 **Nofollow Links** | ${nofollowLinks} |
| 🔥 **High Authority (50+)** | ${highAuthorityLinks} |
| 🆕 **New Links** | ${newLinks} |
| ❌ **Lost Links** | ${lostLinks} |
| 🔴 **Broken Links** | ${brokenLinks} |
| 📈 **Records Found** | ${recordCount} |

${
  topDomains.length > 0
    ? `### 🏆 Top Referring Domains

${topDomains
  .map((domain, index) => {
    const emoji = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '⭐'
    const domainUrl = domain.domain.startsWith('http') ? domain.domain : `https://${domain.domain}`
    return `${emoji} **[${domain.domain}](${domainUrl})** - Authority: ${domain.authority}/100${domain.count > 1 ? ` (${domain.count} pages)` : ''}`
  })
  .join('\n\n')}`
    : ''
}`
}

interface BacklinkData {
  source_url: string
  domain: string
  anchor_text: string
  domain_authority: number
  page_authority: number
  link_type: string
  dofollow: string
  first_seen?: string
  last_seen?: string
  external_links: number
  link_is_lost: string
  link_is_new: string
  broken_status: string
  spam_score: number
}

interface TopDomainData {
  domain: string
  count: number
  authority: number
  backlink: BacklinkData
}

function BacklinkItem({ backlink, className }: { backlink: BacklinkData; className?: string }) {
  const domain = backlink.domain || extractDomainFromUrl(backlink.source_url) || 'Unknown'
  const url = backlink.source_url
  const anchor = backlink.anchor_text || 'No anchor text'
  const authority = Math.max(backlink.domain_authority || 0, backlink.page_authority || 0)
  const isDofollow = backlink.dofollow === 'Yes'
  const isBroken = backlink.broken_status === 'Yes'
  const isNew = backlink.link_is_new === 'Yes'
  const isLost = backlink.link_is_lost === 'Yes'

  const getAuthorityColor = (auth: number) => {
    if (auth >= 70) return 'text-green-600 bg-green-100'
    if (auth >= 40) return 'text-yellow-600 bg-yellow-100'
    return 'text-red-600 bg-red-100'
  }

  return (
    <div
      className={`rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="mb-2 flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="truncate text-sm font-semibold text-gray-900">{domain}</div>
              {isNew && (
                <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">
                  New
                </span>
              )}
              {isLost && (
                <span className="rounded bg-orange-100 px-1.5 py-0.5 text-xs font-medium text-orange-700">
                  Lost
                </span>
              )}
              {isBroken && (
                <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">
                  Broken
                </span>
              )}
            </div>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="block truncate text-xs text-blue-600 hover:underline"
              >
                {url}
              </a>
            )}
          </div>
          <div
            className={`rounded-full px-2 py-1 text-xs font-medium ${getAuthorityColor(authority)}`}
          >
            {authority}
          </div>
        </div>

        <div className="mb-2 text-sm text-gray-700">
          <span className="font-medium">Anchor:</span> {anchor}
        </div>

        <div className="flex items-center justify-between text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <div
              className={`rounded px-1.5 py-0.5 text-xs font-medium ${!isDofollow ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-700'}`}
            >
              {isDofollow ? 'Dofollow' : 'Nofollow'}
            </div>
            {backlink.link_type && (
              <span className="text-xs text-gray-500">{backlink.link_type}</span>
            )}
          </div>
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
            >
              <ExternalLink className="h-3 w-3" />
              <span className="text-xs">View</span>
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function SeoBacklinksDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  // Skip schema validation - use CSV data directly like CSV Reader template tool
  const result = nodeExecution.outputs

  // Handle emit mode - result can be single object or array
  const seoResult = Array.isArray(result) ? result[0] : result
  if (!seoResult) {
    return (
      <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
        <div className="px-4 py-3">
          <div className="text-sm font-semibold text-gray-900">No data available</div>
        </div>
      </div>
    )
  }

  // Get query context from structured result
  const target = seoResult.query?.target
  const targetType = seoResult.query?.targetType
  const action = seoResult.query?.action
  const database = seoResult.query?.database
  const recordCount = seoResult.metadata?.recordCount || 0
  const executedAt = seoResult.metadata?.executedAt
  const csvData = seoResult.csvData

  // Export functions
  const exportToCsv = () => {
    if (!csvData || typeof csvData !== 'string') return

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `seo-backlinks-${target.replace(/[^a-z0-9]/gi, '-')}-${Date.now()}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Context menu actions
  const contextActions: ContextAction[] = csvData
    ? [
        {
          id: 'export-csv',
          label: 'Export CSV',
          icon: <Download className="h-3 w-3" />,
          onClick: exportToCsv,
        },
      ]
    : []

  // Check if this is a GetBacklinkOverview action (single row of metrics)
  const isOverviewAction = action === 'GetBacklinkOverview'

  // Handle GetBacklinkOverview - display as metrics dashboard
  if (isOverviewAction) {
    if (!csvData || typeof csvData !== 'string') {
      return (
        <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
          <div className="px-4 py-3">
            <div className="text-sm font-semibold text-gray-900">No overview data available</div>
          </div>
        </div>
      )
    }

    const { headers, rows } = parseCsv(csvData)
    if (headers.length === 0 || rows.length === 0) {
      return (
        <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
          <div className="px-4 py-3">
            <div className="text-sm font-semibold text-gray-900">No overview data available</div>
          </div>
        </div>
      )
    }

    const objs = rowsToObjects(headers, rows)
    const overview = objs[0] || {}

    // Extract overview metrics - support both lowercase and Title Case
    const backlinksTotal = Number(
      overview['backlinks total'] || overview['Backlinks Total'] || overview['backlinks_total'] || 0
    )
    const domainAuthority = Number(
      overview['domain authority'] ||
        overview['Domain Authority'] ||
        overview['domain_authority'] ||
        0
    )
    const indexedPages = Number(
      overview['indexed pages'] || overview['Indexed Pages'] || overview['indexed_pages'] || 0
    )
    const spamScore = Number(
      overview['spam score'] || overview['Spam Score'] || overview['spam_score'] || 0
    )
    const brokenBacklinks = Number(
      overview['broken backlinks'] ||
        overview['Broken Backlinks'] ||
        overview['broken_backlinks'] ||
        0
    )
    const domains = Number(overview['domains'] || overview['Domains'] || 0)
    const uniqueDomains = Number(
      overview['unique domains'] || overview['Unique Domains'] || overview['unique_domains'] || 0
    )
    const referringPages = Number(
      overview['referring pages'] || overview['Referring Pages'] || overview['referring_pages'] || 0
    )
    const referringIPs = Number(
      overview['referring ips'] || overview['Referring IPs'] || overview['referring_ips'] || 0
    )
    const nofollowLinks = Number(
      overview['nofollow links'] || overview['Nofollow Links'] || overview['nofollow_links'] || 0
    )

    const overviewMarkdown = `## 🔗 Backlink Profile Overview

**Target:** ${target} (${targetType})
**Database:** ${database}
**Executed:** ${new Date(executedAt).toLocaleString()}

### 📊 Backlink Metrics

| Metric | Value |
|--------|-------|
| 🎯 **Total Backlinks** | ${backlinksTotal.toLocaleString()} |
| ⭐ **Domain Authority** | ${domainAuthority}/100 |
| 📄 **Indexed Pages** | ${indexedPages.toLocaleString()} |
| ⚠️ **Spam Score** | ${spamScore}/100 |
| 🔴 **Broken Backlinks** | ${brokenBacklinks.toLocaleString()} |

### 🌐 Link Sources

| Metric | Value |
|--------|-------|
| 🏢 **Referring Domains** | ${domains.toLocaleString()} |
| 🔷 **Unique Domains** | ${uniqueDomains.toLocaleString()} |
| 📄 **Referring Pages** | ${referringPages.toLocaleString()} |
| 🖥️ **Referring IPs** | ${referringIPs.toLocaleString()} |
| 🟡 **NoFollow Links** | ${nofollowLinks.toLocaleString()} |`

    return (
      <div className="space-y-4">
        <ContextMenuWrapper actions={contextActions}>
          <div
            className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm"
            style={{ pointerEvents: 'none' }}
          >
            <div className="px-4 py-3">
              <EnhancedMarkdown
                className="prose prose-sm max-w-none"
                style={{ backgroundColor: 'transparent', color: '#374151' }}
              >
                {overviewMarkdown}
              </EnhancedMarkdown>
            </div>
          </div>
        </ContextMenuWrapper>
      </div>
    )
  }

  // Check if this is a GetReferringDomainsCount action (bulk analysis)
  if (action === 'GetReferringDomainsCount') {
    if (!csvData || typeof csvData !== 'string') {
      return (
        <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
          <div className="px-4 py-3">
            <div className="text-sm font-semibold text-gray-900">
              No referring domains data available
            </div>
          </div>
        </div>
      )
    }

    const { headers, rows } = parseCsv(csvData)
    if (headers.length === 0 || rows.length === 0) {
      return (
        <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
          <div className="px-4 py-3">
            <div className="text-sm font-semibold text-gray-900">
              No referring domains data available
            </div>
          </div>
        </div>
      )
    }

    const objs = rowsToObjects(headers, rows)

    // Calculate totals - support both lowercase and Title Case
    const totalReferringDomains = objs.reduce(
      (sum, item) =>
        sum +
        Number(
          item['referring domains'] || item['Referring Domains'] || item['referring_domains'] || 0
        ),
      0
    )
    const avgReferringDomains =
      objs.length > 0 ? Math.round(totalReferringDomains / objs.length) : 0

    const referringDomainsMarkdown = `## 📊 Bulk Referring Domains Analysis

**Analyzed URLs:** ${objs.length}
**Database:** ${database}
**Executed:** ${new Date(executedAt).toLocaleString()}

### Summary

| Metric | Value |
|--------|-------|
| 📊 **Total Referring Domains** | ${totalReferringDomains.toLocaleString()} |
| 📈 **Average per URL** | ${avgReferringDomains.toLocaleString()} |
| 🔍 **URLs Analyzed** | ${objs.length} |

### Referring Domains by URL

${objs
  .map((item, index) => {
    const url = item['url'] || item['URL'] || 'Unknown'
    const refDomains = Number(
      item['referring domains'] || item['Referring Domains'] || item['referring_domains'] || 0
    )
    return `**${index + 1}. ${url}**  \n${refDomains.toLocaleString()} referring domains`
  })
  .join('\n\n')}`

    return (
      <div className="space-y-4">
        <ContextMenuWrapper actions={contextActions}>
          <div
            className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm"
            style={{ pointerEvents: 'none' }}
          >
            <div className="px-4 py-3">
              <EnhancedMarkdown
                className="prose prose-sm max-w-none"
                style={{ backgroundColor: 'transparent', color: '#374151' }}
              >
                {referringDomainsMarkdown}
              </EnhancedMarkdown>
            </div>
          </div>
        </ContextMenuWrapper>
      </div>
    )
  }

  // Convert CSV to objects using actual API column names (for GetDetailedBacklinks)
  // CSV headers from backend: "Source URL", "Domain", "Page Authority", "Domain Authority", "Anchor Text", "Link Type", "Dofollow", "First Seen", "Last Seen", "External Links", "Link Is Lost", "Link Is New", "Broken Status", "Spam Score"
  // After rowsToObjects, these become lowercase: "source url", "domain", "page authority", etc.
  const backlinks: BacklinkData[] = (() => {
    if (!csvData || typeof csvData !== 'string') return []
    const { headers, rows } = parseCsv(csvData)
    if (headers.length === 0 || rows.length === 0) return []
    const objs = rowsToObjects(headers, rows)

    // Map based on actual CSV column names from backend
    // Try multiple variations to handle case sensitivity and spacing
    return objs.map((o): BacklinkData => {
      // Helper to get value from multiple possible keys
      const getValue = (keys: string[]): string => {
        for (const key of keys) {
          const value = o[key]
          if (value !== undefined && value !== null) {
            const trimmed = String(value).trim()
            if (trimmed !== '') return trimmed
          }
        }
        return ''
      }

      const getNumber = (keys: string[]): number => {
        const val = getValue(keys)
        return val ? Number(val) : 0
      }

      const sourceUrl = getValue(['source url', 'Source URL', 'URL', 'url'])
      const domain = getValue(['domain', 'Domain']) || extractDomainFromUrl(sourceUrl) || ''
      const anchorText = getValue(['anchor text', 'Anchor Text', 'anchor', 'Anchor'])
      const pageAuthority = getNumber(['page authority', 'Page Authority', 'page_authority'])
      const domainAuthority = getNumber([
        'domain authority',
        'Domain Authority',
        'domain_authority',
      ])
      const linkType = getValue(['link type', 'Link Type', 'link_type'])
      const dofollow = getValue(['dofollow', 'Dofollow']) || 'No'
      const firstSeen = getValue(['first seen', 'First Seen', 'first_seen'])
      const lastSeen = getValue(['last seen', 'Last Seen', 'last_seen'])
      const externalLinks = getNumber(['external links', 'External Links', 'external_links'])
      const linkIsLost = getValue(['link is lost', 'Link Is Lost', 'link_is_lost']) || 'No'
      const linkIsNew = getValue(['link is new', 'Link Is New', 'link_is_new']) || 'No'
      const brokenStatus = getValue(['broken status', 'Broken Status', 'broken_status']) || 'No'
      const spamScore = getNumber(['spam score', 'Spam Score', 'spam_score'])

      return {
        source_url: sourceUrl,
        domain: domain,
        anchor_text: anchorText,
        page_authority: pageAuthority,
        domain_authority: domainAuthority,
        link_type: linkType,
        dofollow: dofollow,
        first_seen: firstSeen,
        last_seen: lastSeen,
        external_links: externalLinks,
        link_is_lost: linkIsLost,
        link_is_new: linkIsNew,
        broken_status: brokenStatus,
        spam_score: spamScore,
      }
    })
  })()
  const totalBacklinks = backlinks.length

  if (backlinks.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
        <div className="px-4 py-3">
          <div className="text-sm font-semibold text-gray-900">No backlinks found</div>
          <div className="text-xs text-gray-600">{target}</div>
        </div>
      </div>
    )
  }

  const getTopDomains = (): TopDomainData[] => {
    const domainMap = new Map<
      string,
      { count: number; maxAuthority: number; bestBacklink: BacklinkData }
    >()

    backlinks.forEach((backlink: BacklinkData) => {
      const domain = backlink.domain || 'Unknown'
      const authority = Math.max(backlink.domain_authority || 0, backlink.page_authority || 0)

      if (domainMap.has(domain)) {
        const existing = domainMap.get(domain)!
        existing.count++
        if (authority > existing.maxAuthority) {
          existing.maxAuthority = authority
          existing.bestBacklink = backlink
        }
      } else {
        domainMap.set(domain, { count: 1, maxAuthority: authority, bestBacklink: backlink })
      }
    })

    return Array.from(domainMap.entries())
      .map(([domain, data]) => ({
        domain,
        count: data.count,
        authority: data.maxAuthority,
        backlink: data.bestBacklink,
      }))
      .sort((a, b) => b.authority - a.authority)
      .slice(0, 4)
  }

  const topDomains = getTopDomains()
  const summaryMarkdown = generateBacklinksSummaryMarkdown(
    target,
    targetType,
    action,
    database,
    totalBacklinks,
    recordCount,
    executedAt,
    backlinks,
    topDomains
  )

  return (
    <div className="space-y-4">
      <ContextMenuWrapper actions={contextActions}>
        <div
          className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm"
          style={{ pointerEvents: 'none' }}
        >
          <div className="px-4 py-3">
            <EnhancedMarkdown
              className="prose prose-sm max-w-none"
              style={{ backgroundColor: 'transparent', color: '#374151' }}
            >
              {summaryMarkdown}
            </EnhancedMarkdown>

            <details className="mt-4 w-full" open={isExpanded}>
              <summary
                className="flex w-full cursor-pointer list-none items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                onClick={(e) => {
                  e.preventDefault()
                  setIsExpanded(!isExpanded)
                }}
              >
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                />
                <span>Individual Backlinks ({backlinks.length})</span>
              </summary>
              {isExpanded && (
                <div className="mt-3 space-y-3">
                  {backlinks.map((backlink: BacklinkData, index: number) => (
                    <BacklinkItem
                      key={`${backlink.source_url || 'unknown'}-${backlink.anchor_text || 'no-anchor'}-${index}`}
                      backlink={backlink}
                    />
                  ))}
                </div>
              )}
            </details>
          </div>
        </div>
      </ContextMenuWrapper>
    </div>
  )
}

function downloadSeoBacklinks(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function seoBacklinksToMarkdown(nodeExecution: NodeExecution): string {
  // Skip schema validation - use CSV data directly like CSV Reader template tool
  const result = nodeExecution.outputs

  // Handle emit mode - result can be single object or array
  const seoResult = Array.isArray(result) ? result[0] : result
  if (!seoResult) {
    return '## SEO Research - Backlinks Analysis\n\nNo data available.\n\n---\n'
  }

  const target = seoResult.query.target
  const targetType = seoResult.query.targetType
  const action = seoResult.query.action
  const database = seoResult.query.database
  const executedAt = seoResult.metadata.executedAt
  const csvData = seoResult.csvData

  // Handle GetBacklinkOverview action
  if (action === 'GetBacklinkOverview') {
    if (!csvData || typeof csvData !== 'string') {
      return '## SEO Research - Backlink Profile Overview\n\nNo overview data available.\n\n---\n'
    }

    const { headers, rows } = parseCsv(csvData)
    if (headers.length === 0 || rows.length === 0) {
      return '## SEO Research - Backlink Profile Overview\n\nNo overview data available.\n\n---\n'
    }

    const objs = rowsToObjects(headers, rows)
    const overview = objs[0] || {}

    // Extract overview metrics - support both lowercase and Title Case
    const backlinksTotal = Number(
      overview['backlinks total'] || overview['Backlinks Total'] || overview['backlinks_total'] || 0
    )
    const domainAuthority = Number(
      overview['domain authority'] ||
        overview['Domain Authority'] ||
        overview['domain_authority'] ||
        0
    )
    const indexedPages = Number(
      overview['indexed pages'] || overview['Indexed Pages'] || overview['indexed_pages'] || 0
    )
    const spamScore = Number(
      overview['spam score'] || overview['Spam Score'] || overview['spam_score'] || 0
    )
    const brokenBacklinks = Number(
      overview['broken backlinks'] ||
        overview['Broken Backlinks'] ||
        overview['broken_backlinks'] ||
        0
    )
    const domains = Number(overview['domains'] || overview['Domains'] || 0)
    const uniqueDomains = Number(
      overview['unique domains'] || overview['Unique Domains'] || overview['unique_domains'] || 0
    )
    const referringPages = Number(
      overview['referring pages'] || overview['Referring Pages'] || overview['referring_pages'] || 0
    )
    const referringIPs = Number(
      overview['referring ips'] || overview['Referring IPs'] || overview['referring_ips'] || 0
    )
    const nofollowLinks = Number(
      overview['nofollow links'] || overview['Nofollow Links'] || overview['nofollow_links'] || 0
    )

    return `## SEO Research - Backlink Profile Overview

**Target:** ${target} (${targetType})
**Database:** ${database}
**Executed:** ${new Date(executedAt).toLocaleString()}

### Backlink Metrics

| Metric | Value |
|--------|-------|
| Total Backlinks | ${backlinksTotal.toLocaleString()} |
| Domain Authority | ${domainAuthority}/100 |
| Indexed Pages | ${indexedPages.toLocaleString()} |
| Spam Score | ${spamScore}/100 |
| Broken Backlinks | ${brokenBacklinks.toLocaleString()} |

### Link Sources

| Metric | Value |
|--------|-------|
| Referring Domains | ${domains.toLocaleString()} |
| Unique Domains | ${uniqueDomains.toLocaleString()} |
| Referring Pages | ${referringPages.toLocaleString()} |
| Referring IPs | ${referringIPs.toLocaleString()} |
| NoFollow Links | ${nofollowLinks.toLocaleString()} |

---
`
  }

  // Handle GetReferringDomainsCount action
  if (action === 'GetReferringDomainsCount') {
    if (!csvData || typeof csvData !== 'string') {
      return '## SEO Research - Bulk Referring Domains Analysis\n\nNo referring domains data available.\n\n---\n'
    }

    const { headers, rows } = parseCsv(csvData)
    if (headers.length === 0 || rows.length === 0) {
      return '## SEO Research - Bulk Referring Domains Analysis\n\nNo referring domains data available.\n\n---\n'
    }

    const objs = rowsToObjects(headers, rows)

    // Calculate totals - support both lowercase and Title Case
    const totalReferringDomains = objs.reduce(
      (sum, item) =>
        sum +
        Number(
          item['referring domains'] || item['Referring Domains'] || item['referring_domains'] || 0
        ),
      0
    )
    const avgReferringDomains =
      objs.length > 0 ? Math.round(totalReferringDomains / objs.length) : 0

    const tableHeader = `| URL | Referring Domains |
|-----|------------------|`

    const tableRows = objs
      .map((item) => {
        const url = item['url'] || item['URL'] || 'Unknown'
        const refDomains = Number(
          item['referring domains'] || item['Referring Domains'] || item['referring_domains'] || 0
        )
        return `| ${url} | ${refDomains.toLocaleString()} |`
      })
      .join('\n')

    return `## SEO Research - Bulk Referring Domains Analysis

**Analyzed URLs:** ${objs.length}
**Database:** ${database}
**Executed:** ${new Date(executedAt).toLocaleString()}

### Summary

- Total Referring Domains: ${totalReferringDomains.toLocaleString()}
- Average per URL: ${avgReferringDomains.toLocaleString()}
- URLs Analyzed: ${objs.length}

### Results

${tableHeader}
${tableRows}

---
`
  }

  // Parse backlinks from CSV data (for GetDetailedBacklinks and other actions)
  const backlinks: BacklinkData[] = (() => {
    if (!csvData || typeof csvData !== 'string') return []
    const { headers, rows } = parseCsv(csvData)
    if (headers.length === 0 || rows.length === 0) return []
    const objs = rowsToObjects(headers, rows)

    return objs.map((o): BacklinkData => {
      // Helper to get value from multiple possible keys
      const getValue = (keys: string[]): string => {
        for (const key of keys) {
          if (o[key] !== undefined && o[key] !== '') return o[key]
        }
        return ''
      }

      const getNumber = (keys: string[]): number => {
        const val = getValue(keys)
        return val ? Number(val) : 0
      }

      const sourceUrl = getValue(['source url', 'Source URL', 'URL', 'url'])
      const domain = getValue(['domain', 'Domain']) || extractDomainFromUrl(sourceUrl) || ''
      const anchorText = getValue(['anchor text', 'Anchor Text', 'anchor', 'Anchor'])
      const pageAuthority = getNumber(['page authority', 'Page Authority', 'page_authority'])
      const domainAuthority = getNumber([
        'domain authority',
        'Domain Authority',
        'domain_authority',
      ])
      const linkType = getValue(['link type', 'Link Type', 'link_type'])
      const dofollow = getValue(['dofollow', 'Dofollow']) || 'No'
      const firstSeen = getValue(['first seen', 'First Seen', 'first_seen'])
      const lastSeen = getValue(['last seen', 'Last Seen', 'last_seen'])
      const externalLinks = getNumber(['external links', 'External Links', 'external_links'])
      const linkIsLost = getValue(['link is lost', 'Link Is Lost', 'link_is_lost']) || 'No'
      const linkIsNew = getValue(['link is new', 'Link Is New', 'link_is_new']) || 'No'
      const brokenStatus = getValue(['broken status', 'Broken Status', 'broken_status']) || 'No'
      const spamScore = getNumber(['spam score', 'Spam Score', 'spam_score'])

      return {
        source_url: sourceUrl,
        domain: domain,
        anchor_text: anchorText,
        page_authority: pageAuthority,
        domain_authority: domainAuthority,
        link_type: linkType,
        dofollow: dofollow,
        first_seen: firstSeen,
        last_seen: lastSeen,
        external_links: externalLinks,
        link_is_lost: linkIsLost,
        link_is_new: linkIsNew,
        broken_status: brokenStatus,
        spam_score: spamScore,
      }
    })
  })()

  if (backlinks.length === 0) {
    return `## SEO Research - Backlinks Analysis

**Target:** ${target}

No backlinks found.

---
`
  }

  const tableHeader = `| Domain | Authority | Anchor Text | Type | Status | URL |
|--------|-----------|-------------|------|--------|-----|`

  const tableRows = backlinks
    .map((backlink: BacklinkData) => {
      const domain = backlink.domain || 'Unknown'
      const authority = Math.max(backlink.domain_authority || 0, backlink.page_authority || 0)
      const authorityDisplay = `${authority}/100`
      const anchor = (backlink.anchor_text || 'No anchor text')
        .replace(/\|/g, '\\|')
        .replace(/\n/g, ' ')
        .slice(0, 40)
      const anchorDisplay = anchor.length === 40 ? anchor + '...' : anchor
      const type = backlink.dofollow === 'Yes' ? 'Dofollow' : 'Nofollow'

      // Status badges
      const statusParts = []
      if (backlink.link_is_new === 'Yes') statusParts.push('New')
      if (backlink.link_is_lost === 'Yes') statusParts.push('Lost')
      if (backlink.broken_status === 'Yes') statusParts.push('Broken')
      const status = statusParts.length > 0 ? statusParts.join(', ') : 'Active'

      const url = backlink.source_url
      const urlDisplay = url ? `[Visit](${url})` : '-'

      return `| ${domain} | ${authorityDisplay} | ${anchorDisplay} | ${type} | ${status} | ${urlDisplay} |`
    })
    .join('\n')

  const detailedAnchors = backlinks
    .filter((backlink: BacklinkData) => backlink.anchor_text?.length > 40)
    .slice(0, 10) // Limit to first 10 detailed anchors
    .map((backlink: BacklinkData) => {
      const domain = backlink.domain || 'Unknown'
      const anchor = backlink.anchor_text || ''
      return `### ${domain}\n\n**Full Anchor Text:** ${anchor}`
    })
    .join('\n\n')

  return `## SEO Research - Backlinks Analysis (${backlinks.length} backlinks)

**Target:** ${target}

${tableHeader}
${tableRows}

${detailedAnchors ? `\n## Detailed Anchor Texts\n\n${detailedAnchors}\n` : ''}
---
`
}

export const SeoBacklinksDisplay = createWorkflowToolComponent(
  SeoBacklinksDisplayComponent,
  downloadSeoBacklinks,
  seoBacklinksToMarkdown
)
